import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toLocalYmd } from "../../../utils/dateTime";
import { useReactToPrint } from "react-to-print";
import { useDispatch } from "react-redux";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import {
  ErrorToast,
  SuccessToast,
  WarningToast,
} from "../../../utils/ShowToast";
import CustomerSelector from "../../../components/Selector/CustomerSelector";
import CreateInvoiceItemsTable, {
  getCommittedInvoiceItems,
} from "../../../components/InoviceAddItem/CreateInvoiceItemsTable";
import {
  CreateCustomerInvoiceApi,
  CreateCustomerReturnApi,
  CheckInvoiceNoAvailabliltyAPI,
} from "../../../ApiRequests";
import SaleType from "../../../components/Input/SaleType";
import InvoiceCustomerType from "../../../components/Input/InvoiceCustomerType";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ThermalPrintModeSelect from "../../../components/Thermal/ThermalPrintModeSelect";
import ThermalCustomerInvoice from "../../../components/Reports/ThermalCustomerInvoice";
import {
  FiPackage,
  FiPrinter,
  FiSend,
  FiUser,
  FiXCircle,
} from "react-icons/fi";

const userData = JSON.parse(localStorage.getItem("user")) || {};
const { branchId } = userData;

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const defaultThermalPrintMode = (guest) =>
  guest ? "hideItemPrices" : "qtyOnly";

const PosField = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1 min-w-0 ${className}`}>
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted">
      {label}
    </span>
    {children}
  </label>
);

const posInputClass =
  "w-full h-10 px-3 text-sm font-semibold border border-line rounded-lg outline-none bg-card text-ink placeholder:text-muted focus:border-main focus:ring-2 focus:ring-main/15";

const InvoiceMetaFields = ({
  invoiceNoInputRef,
  invoiceNo,
  onInvoiceNoChange,
  onInvoiceNoBlur,
  date,
  onDateChange,
  description,
  onDescriptionChange,
  isGuest = false,
}) => {
  const descriptionInputRef = useRef(null);

  return (
    <>
      <PosField label="Invoice #">
        <input
          ref={invoiceNoInputRef}
          type="number"
          value={invoiceNo}
          onChange={(e) => onInvoiceNoChange(e.target.value)}
          onBlur={onInvoiceNoBlur}
          placeholder="Auto"
          className={posInputClass}
        />
      </PosField>
      <PosField label="Date">
        <input
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          onKeyDown={(e) => {
            // Native date input has year/month/day segments — one Tab jumps to next field
            if (e.key !== "Tab") return;
            e.preventDefault();
            if (e.shiftKey) {
              invoiceNoInputRef?.current?.focus({ preventScroll: true });
            } else {
              descriptionInputRef.current?.focus({ preventScroll: true });
            }
          }}
          className={posInputClass}
          title="Click to change date · Tab to continue"
        />
      </PosField>
      <PosField label={isGuest ? "Description (optional)" : "Description"}>
        <input
          ref={descriptionInputRef}
          type="text"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={
            isGuest
              ? "Optional — blank = Walk In Customer"
              : "Required — e.g. Marka / note"
          }
          className={posInputClass}
        />
      </PosField>
    </>
  );
};

const SummaryLine = ({ label, value, highlight, bold, large }) => (
  <div
    className={`flex justify-between items-baseline gap-3 ${
      bold ? "pt-3 mt-2 border-t-2 border-main" : "py-1.5"
    }`}
  >
    <span
      className={`${
        bold ? "text-sm font-bold text-ink" : "text-xs text-muted uppercase tracking-wide"
      }`}
    >
      {label}
    </span>
    <span
      className={`tabular-nums font-bold ${
        large ? "text-2xl text-main" : bold ? "text-lg text-main" : "text-sm text-ink"
      } ${highlight ? "text-success" : ""}`}
    >
      {value}
    </span>
  </div>
);

const CreateInvoice = () => {
  const [SelectedCustomer, setSelectedCustomer] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const [guestAddress, setGuestAddress] = useState("");
  const [Payment, setPayment] = useState("");
  const [itemRows, setItemRows] = useState([]);
  const [Discount, setDiscount] = useState(0);
  const [CurDate, setCurDate] = useState(
    toLocalYmd()
  );
  const [InvoiceNo, setInvoiceNo] = useState("");
  const [Description, setDescription] = useState("");
  const dispatch = useDispatch();
  const [Loading, setLoading] = useState(false);
  const [saleType, setSaleType] = useState("SALE");
  const [invoiceCustomerType, setInvoiceCustomerType] = useState("CUSTOMER");

  const [thermalReceiptData, setThermalReceiptData] = useState(null);
  const [thermalPrintMode, setThermalPrintMode] = useState("qtyOnly");
  const [customerSelectorOpenTrigger, setCustomerSelectorOpenTrigger] =
    useState(0);
  const invoiceNoInputRef = useRef(null);
  const guestNameRef = useRef(null);
  const guestContactRef = useRef(null);
  const guestAddressRef = useRef(null);
  const discountRef = useRef(null);
  const paymentRef = useRef(null);
  const isGuestRef = useRef(false);
  const lastInvoiceNoFocusCustomerRef = useRef(null);
  const thermalPrintRef = useRef(null);
  const silentPrintPendingRef = useRef(false);
  const lastEnterAtRef = useRef(0);
  const loadingRef = useRef(false);
  const DOUBLE_ENTER_MS = 450;

  const focusInvoiceNoField = useCallback(() => {
    invoiceNoInputRef.current?.focus({ preventScroll: true });
  }, []);

  const focusFirstItemCode = useCallback(() => {
    const input = document.querySelector(
      "[data-invoice-items-table] tbody tr:first-child input"
    );
    input?.focus({ preventScroll: true });
  }, []);

  const isGuest =
    invoiceCustomerType === "GUEST" || invoiceCustomerType === "WALK_IN";
  isGuestRef.current = isGuest;

  const handleInvoiceCustomerTypeChange = useCallback((nextType) => {
    const guest = nextType === "GUEST" || nextType === "WALK_IN";
    setInvoiceCustomerType(nextType);
    // After Guest/Customer UI swaps, jump to Guest Name or open Customer Selector
    setTimeout(() => {
      if (guest) {
        guestNameRef.current?.focus({ preventScroll: true });
        guestNameRef.current?.select?.();
      } else {
        setCustomerSelectorOpenTrigger((n) => n + 1);
      }
    }, 50);
  }, []);

  const committedItems = useMemo(
    () => getCommittedInvoiceItems(itemRows),
    [itemRows]
  );

  const billTotal = useMemo(
    () => committedItems.reduce((t, i) => t + Number(i.amount || 0), 0),
    [committedItems]
  );

  const purchaseTotal = useMemo(
    () =>
      committedItems.reduce(
        (t, i) => t + Number(i.purchase || 0) * Number(i.qty || 0),
        0
      ),
    [committedItems]
  );

  const totalQty = useMemo(
    () => committedItems.reduce((t, i) => t + Number(i.qty || 0), 0),
    [committedItems]
  );

  const shoppingTotal = useMemo(
    () =>
      committedItems
        .filter((dt) => dt.unit === "SH")
        .reduce((t, i) => t + Number(i.amount || 0), 0),
    [committedItems]
  );

  const netBill = useMemo(
    () => Math.max(0, billTotal - Number(Discount || 0)),
    [billTotal, Discount]
  );

  const profit = billTotal - purchaseTotal;

  const remaining = useMemo(() => {
    if (!SelectedCustomer || isGuest) return 0;
    return (
      Number(SelectedCustomer.recieveable || 0) +
      billTotal -
      Number(Payment || 0) -
      Number(Discount || 0)
    );
  }, [SelectedCustomer, billTotal, Payment, Discount, isGuest]);

  const receiptCustomer = useMemo(() => {
    if (isGuest) {
      return {
        name: guestName.trim(),
        contact: guestContact,
        address: guestAddress,
      };
    }
    if (!SelectedCustomer) return null;
    return {
      ...SelectedCustomer,
      contact: guestContact || SelectedCustomer.contact,
      address: guestAddress || SelectedCustomer.address,
    };
  }, [SelectedCustomer, guestContact, guestAddress, guestName, isGuest]);

  const buildReceiptData = () => ({
    customer: receiptCustomer,
    invoice_no: InvoiceNo,
    date: CurDate,
    desc: Description,
    items: committedItems,
    discount: Number(Discount) || 0,
    payment: Number(Payment) || 0,
    billTotal,
    oldBalance: SelectedCustomer?.recieveable ?? 0,
    remaining,
    saleType,
    invoiceCustomerType,
    printMode: thermalPrintMode,
  });

  const prepareNextInvoice = useCallback(() => {
    lastInvoiceNoFocusCustomerRef.current = null;
    setSelectedCustomer("");
    setGuestName("");
    setGuestContact("");
    setGuestAddress("");
    setDescription("");
    setItemRows([]);
    setInvoiceNo("");
    setCurDate(toLocalYmd());
    setPayment("");
    setDiscount(0);
    setInvoiceCustomerType("CUSTOMER");
    setSaleType("SALE");
    setThermalPrintMode(defaultThermalPrintMode(false));
    setThermalReceiptData(null);
    setCustomerSelectorOpenTrigger((n) => n + 1);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  }, []);

  const handleSilentThermalPrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `Invoice-${thermalReceiptData?.invoice_no || "receipt"}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      silentPrintPendingRef.current = false;
      prepareNextInvoice();
    },
  });

  const queueSilentPrint = (receipt) => {
    silentPrintPendingRef.current = true;
    setThermalReceiptData(receipt);
  };

  useEffect(() => {
    if (!silentPrintPendingRef.current || !thermalReceiptData) return;
    silentPrintPendingRef.current = false;
    const timer = setTimeout(() => handleSilentThermalPrint(), 350);
    return () => clearTimeout(timer);
  }, [thermalReceiptData, handleSilentThermalPrint]);

  const resetForm = () => {
    lastInvoiceNoFocusCustomerRef.current = null;
    setSelectedCustomer("");
    setGuestName("");
    setGuestContact("");
    setGuestAddress("");
    setDescription("");
    setItemRows([]);
    setInvoiceNo("");
    setCurDate(toLocalYmd());
    setPayment("");
    setDiscount(0);
    setInvoiceCustomerType("CUSTOMER");
    setSaleType("SALE");
    setThermalPrintMode(defaultThermalPrintMode(false));
    setThermalReceiptData(null);
    silentPrintPendingRef.current = false;
  };

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (SelectedCustomer && !isGuest) {
      setGuestContact(SelectedCustomer.contact || "");
      setGuestAddress(SelectedCustomer.address || "");
    }
  }, [SelectedCustomer, isGuest]);

  useEffect(() => {
    if (isGuest) {
      setSelectedCustomer("");
    } else {
      setGuestName("");
      setGuestContact("");
      setGuestAddress("");
    }
  }, [isGuest]);

  useEffect(() => {
    if (isGuest && (saleType === "SALE" || saleType === "RETURN")) {
      setPayment(String(netBill));
    }
  }, [isGuest, saleType, netBill]);

  useEffect(() => {
    setThermalPrintMode(defaultThermalPrintMode(isGuest));
  }, [isGuest]);

  useEffect(() => {
    if (isGuest || !SelectedCustomer?._id) return;
    const customerId = String(SelectedCustomer._id);
    if (lastInvoiceNoFocusCustomerRef.current === customerId) return;
    lastInvoiceNoFocusCustomerRef.current = customerId;

    const timer = setTimeout(() => {
      if (document.activeElement?.closest?.("[data-invoice-items-table]")) {
        return;
      }
      focusInvoiceNoField();
    }, 120);
    return () => clearTimeout(timer);
  }, [SelectedCustomer?._id, isGuest, focusInvoiceNoField]);

  const onSubmit = async (e, type) => {
    e?.preventDefault?.();
    // Sync lock — shortcuts can fire before React re-renders Loading=true
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    if (!isGuest && saleType === "RETURN" && !SelectedCustomer) {
      WarningToast("Select Customer for return!");
    } else if (!isGuest && saleType === "SALE" && !SelectedCustomer) {
      WarningToast("Select Customer!");
    } else if (isGuest && !guestName.trim()) {
      WarningToast("Enter Guest Name!");
    } else if (!isGuest && !Description.trim()) {
      WarningToast("Enter Description!");
    } else if (!CurDate) WarningToast("Enter a valid Date");
    else if (!committedItems.length) WarningToast("Add at least one item!");
    else if (
      isGuest &&
      saleType === "SALE" &&
      Number(Payment || 0) < netBill
    ) {
      WarningToast("Guest sale requires full cash payment");
    } else if (
      isGuest &&
      saleType === "RETURN" &&
      Number(Payment || 0) < netBill
    ) {
      WarningToast("Guest return requires full cash refund");
    } else {
      try {
        let response;
        const payloadBase = {
          total_amount: billTotal - Number(Discount),
          discount: Number(Discount),
          items: committedItems,
          date: CurDate,
          invoice_no: InvoiceNo.trim(),
          desc: isGuest
            ? Description.trim() || "Walk In Customer"
            : Description.trim(),
          branchId,
        };

        if (saleType === "SALE") {
          response = await CreateCustomerInvoiceApi({
            ...payloadBase,
            ...(isGuest
              ? {
                  invoice_customer_type: "GUEST",
                  guest_name: guestName.trim(),
                  guest_contact: guestContact,
                  guest_address: guestAddress,
                }
              : {
                  customerId: SelectedCustomer._id,
                  invoice_customer_type: "CUSTOMER",
                }),
            payment: Payment,
          });
        } else {
          response = await CreateCustomerReturnApi({
            ...payloadBase,
            ...(isGuest
              ? {
                  invoice_customer_type: "GUEST",
                  guest_name: guestName.trim(),
                  guest_contact: guestContact,
                  guest_address: guestAddress,
                  payment: Payment,
                }
              : {
                  customerId: SelectedCustomer._id,
                  invoice_customer_type: "CUSTOMER",
                }),
          });
        }

        if (response.data.success) {
          SuccessToast("Invoice added successfully");
          if (type === 2) {
            const saved = response.data?.data?.payload;
            queueSilentPrint({
              ...buildReceiptData(),
              invoice_no: saved?.invoice_no ?? "",
              auto_invoice_no: saved?.auto_invoice_no ?? "",
            });
          } else {
            prepareNextInvoice();
          }
        } else {
          ErrorToast(response.data.error?.msg);
        }
      } catch (err) {
        ErrorToast(err.response?.data?.error?.msg || "Something went wrong");
      }
    }
    loadingRef.current = false;
    setLoading(false);
  };

  const handleClear = () => {
    if (window.confirm("Clear all invoice data?")) resetForm();
  };

  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const setSaleTypeRef = useRef(setSaleType);
  setSaleTypeRef.current = setSaleType;
  const setInvoiceCustomerTypeRef = useRef(handleInvoiceCustomerTypeChange);
  setInvoiceCustomerTypeRef.current = handleInvoiceCustomerTypeChange;

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      const inTextarea = tag === "textarea";
      const isFunctionKey = /^F\d{1,2}$/.test(e.key);
      const isSubmitShortcut =
        ((e.ctrlKey || e.metaKey) && e.key === "Enter") ||
        (e.key === "Enter" &&
          !e.shiftKey &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey &&
          !inTextarea);

      // While saving: block POS shortcuts / re-submit (buttons already disabled)
      if (loadingRef.current && (isFunctionKey || isSubmitShortcut)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (e.key === "F5") {
        e.preventDefault();
        setSaleTypeRef.current("SALE");
        return;
      }
      if (e.key === "F6") {
        e.preventDefault();
        setSaleTypeRef.current("RETURN");
        return;
      }
      if (e.key === "F7") {
        e.preventDefault();
        isGuestRef.current = false;
        setInvoiceCustomerTypeRef.current("CUSTOMER");
        return;
      }
      if (e.key === "F8") {
        e.preventDefault();
        isGuestRef.current = true;
        setInvoiceCustomerTypeRef.current("GUEST");
        return;
      }
      if (e.key === "F2") {
        e.preventDefault();
        if (isGuestRef.current) {
          guestNameRef.current?.focus({ preventScroll: true });
        } else {
          setCustomerSelectorOpenTrigger((n) => n + 1);
        }
        return;
      }
      if (e.key === "F4") {
        e.preventDefault();
        focusFirstItemCode();
        return;
      }
      if (e.key === "F1" && isGuestRef.current) {
        e.preventDefault();
        guestContactRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "F3" && isGuestRef.current) {
        e.preventDefault();
        guestAddressRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "F9") {
        e.preventDefault();
        discountRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "F10") {
        e.preventDefault();
        paymentRef.current?.focus({ preventScroll: true });
        return;
      }
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !inTextarea
      ) {
        const inItemsTable = e.target?.closest?.("[data-invoice-items-table]");
        if (!inItemsTable) {
          const now = Date.now();
          const last = lastEnterAtRef.current;
          if (last && now - last <= DOUBLE_ENTER_MS) {
            e.preventDefault();
            lastEnterAtRef.current = 0;
            if (!loadingRef.current) {
              submitRef.current({ preventDefault: () => {} }, 1);
            }
            return;
          }
          lastEnterAtRef.current = now;
        } else {
          lastEnterAtRef.current = 0;
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !inTextarea) {
        e.preventDefault();
        if (loadingRef.current) return;
        submitRef.current(
          { preventDefault: () => {} },
          e.shiftKey ? 2 : 1
        );
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [focusFirstItemCode]);

  const handleInvoiceNoBlur = async () => {
    const billNo = String(InvoiceNo ?? "").trim();
    if (!billNo) return;

    try {
      const result = await CheckInvoiceNoAvailabliltyAPI(billNo);
      if (result.data.success) {
        SuccessToast(result.data.data.msg);
      } else {
        WarningToast("Invoice already exists. Please use another number.");
        setInvoiceNo("");
      }
    } catch (err) {
      ErrorToast(
        err.response?.data?.error?.msg ||
          "Something went wrong while checking invoice"
      );
      if (err.response?.data?.error?.msg === "Invoice is not Available") {
        setInvoiceNo("");
      }
    }
  };

  const partyLabel =
    saleType === "RETURN" ? "Return from" : "Sale to";

  const itemsSubtitle = isGuest
    ? saleType === "RETURN"
      ? "Returning items from guest"
      : "Guest sale — scan or type item code"
    : SelectedCustomer
    ? saleType === "RETURN"
      ? `Return from ${SelectedCustomer.name}`
      : `${SelectedCustomer.name} — type code, Enter, then Qty`
    : "Select customer, then add items";

  return (
    <div
      className="flex flex-col min-h-[calc(100vh-4rem)] font-quicksand -mx-2 sm:-mx-4"
      data-pos-invoice
    >
      {/* ── POS top bar ── */}
      <header className="shrink-0 border-b border-line bg-card shadow-soft">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 px-4 py-3 items-stretch">
          <div className="flex shrink-0 flex-row lg:flex-col gap-4 lg:gap-3 items-start justify-start border-b lg:border-b-0 lg:border-r border-line/80 pb-3 lg:pb-0 lg:pr-6">
            <div className="flex flex-col gap-0.5 items-start">
              <SaleType saleType={saleType} setSaleType={setSaleType} />
              <span className="text-[10px] font-semibold text-muted hidden sm:block">
                F5 Sale · F6 Return
              </span>
            </div>
            <div className="flex flex-col gap-0.5 items-start">
              <InvoiceCustomerType
                customerType={invoiceCustomerType}
                setCustomerType={handleInvoiceCustomerTypeChange}
                label={partyLabel}
              />
              <span className="text-[10px] font-semibold text-muted hidden sm:block">
                F7 Customer · F8 Guest · F2 Name / Select
              </span>
            </div>
          </div>

          <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-3 gap-y-3 content-start">
            <PosField label={isGuest ? "Guest name" : "Customer"}>
              {isGuest ? (
                <input
                  ref={guestNameRef}
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Required — guest name"
                  className={posInputClass}
                />
              ) : (
                <CustomerSelector
                  compact
                  SelectedCustomer={SelectedCustomer}
                  setSelectedCustomer={setSelectedCustomer}
                  openTrigger={customerSelectorOpenTrigger}
                  onCustomerSelected={() => {
                    requestAnimationFrame(() => {
                      setTimeout(focusInvoiceNoField, 0);
                    });
                  }}
                />
              )}
            </PosField>
            <InvoiceMetaFields
              invoiceNoInputRef={invoiceNoInputRef}
              invoiceNo={InvoiceNo}
              onInvoiceNoChange={setInvoiceNo}
              onInvoiceNoBlur={handleInvoiceNoBlur}
              date={CurDate}
              onDateChange={setCurDate}
              description={Description}
              onDescriptionChange={setDescription}
              isGuest={isGuest}
            />
          </div>
        </div>
      </header>

      {/* ── Main: items + summary ── */}
      <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
        <section className="flex-1 min-w-0 flex flex-col min-h-[320px] lg:min-h-0 border-b lg:border-b-0 lg:border-r border-line bg-page">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-line bg-card/80 shrink-0">
            <FiPackage className="text-main shrink-0" size={16} />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-ink">
                Line items
              </p>
              <p className="text-[11px] text-muted truncate">{itemsSubtitle}</p>
            </div>
            <span className="ml-auto text-xs font-bold tabular-nums text-main bg-main/10 px-2.5 py-1 rounded-full">
              {committedItems.length} item{committedItems.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-3 sm:p-4 scrollable">
            <CreateInvoiceItemsTable
              items={itemRows}
              setItems={setItemRows}
              saleType={saleType}
              customerAccountType={
                isGuest ? undefined : SelectedCustomer?.type
              }
              variant="pos"
            />
          </div>
        </section>

        <aside className="w-full lg:w-[min(100%,320px)] shrink-0 flex flex-col bg-card border-line">
          <div className="px-4 py-3 border-b border-line bg-subtle/60">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">
              Bill summary
            </p>
          </div>

          <div className="flex-1 overflow-y-auto scrollable px-4 py-3 space-y-0.5">
            <SummaryLine label="Qty" value={fmt(totalQty)} />
            <SummaryLine label="Sale" value={`Rs ${fmt(billTotal)}`} />
            {shoppingTotal > 0 && (
              <SummaryLine label="Shopping" value={`Rs ${fmt(shoppingTotal)}`} />
            )}
            <SummaryLine label="Purchase" value={`Rs ${fmt(purchaseTotal)}`} />
            <SummaryLine
              label="Profit"
              value={`Rs ${fmt(profit)}`}
              highlight={profit >= 0}
            />
            <SummaryLine
              label="Net bill"
              value={`Rs ${fmt(netBill)}`}
              bold
              large
            />
            {!isGuest && SelectedCustomer && saleType === "SALE" && (
              <>
                <SummaryLine
                  label="Old balance"
                  value={`Rs ${fmt(SelectedCustomer.recieveable)}`}
                />
                <SummaryLine
                  label="Remaining"
                  value={`Rs ${fmt(remaining)}`}
                  bold
                />
              </>
            )}
          </div>

          <div className="border-t border-line px-4 py-3 bg-subtle/30">
            <ThermalPrintModeSelect
              compact
              value={thermalPrintMode}
              onChange={setThermalPrintMode}
            />
          </div>
        </aside>
      </div>

      {/* ── Sticky action bar ── */}
      <footer className="shrink-0 sticky bottom-0 z-20 border-t border-line bg-card/95 backdrop-blur-sm px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3 min-w-0 flex-1">
            {isGuest && (
              <>
                <PosField label="Mobile Number (optional)" className="w-[min(100%,150px)]">
                  <input
                    ref={guestContactRef}
                    type="text"
                    value={guestContact}
                    onChange={(e) => setGuestContact(e.target.value)}
                    placeholder="Optional — mobile"
                    title="F1"
                    className={posInputClass}
                  />
                </PosField>
                <PosField label="Address" className="min-w-[140px] flex-1 max-w-[280px]">
                  <input
                    ref={guestAddressRef}
                    type="text"
                    value={guestAddress}
                    onChange={(e) => setGuestAddress(e.target.value)}
                    placeholder="Address"
                    title="F3"
                    className={posInputClass}
                  />
                </PosField>
              </>
            )}
            <PosField label="Discount" className="w-[min(100%,140px)]">
              <input
                ref={discountRef}
                type="number"
                value={Discount}
                onChange={(e) => setDiscount(e.target.value)}
                placeholder="0"
                title="F9"
                className={posInputClass}
              />
            </PosField>
            {(saleType === "SALE" || (saleType === "RETURN" && isGuest)) && (
              <PosField
                label={
                  isGuest
                    ? saleType === "RETURN"
                      ? "Cash refund"
                      : "Cash payment"
                    : "Payment"
                }
                className="w-[min(100%,140px)]"
              >
                <input
                  ref={paymentRef}
                  type="number"
                  value={Payment}
                  onChange={(e) => setPayment(e.target.value)}
                  placeholder="0"
                  title="F10"
                  disabled={isGuest}
                  className={posInputClass}
                />
              </PosField>
            )}
            <p className="text-[11px] text-muted hidden lg:block pb-2.5">
              <FiUser className="inline mr-1 -mt-0.5" size={12} />
              {isGuest ? (
                <>
                  <kbd className="px-1 py-0.5 rounded bg-subtle border border-line text-[10px] font-mono">
                    F1
                  </kbd>{" "}
                  Contact ·{" "}
                  <kbd className="px-1 py-0.5 rounded bg-subtle border border-line text-[10px] font-mono">
                    F3
                  </kbd>{" "}
                  Address ·{" "}
                </>
              ) : null}
              <kbd className="px-1 py-0.5 rounded bg-subtle border border-line text-[10px] font-mono">
                F9
              </kbd>{" "}
              Discount ·{" "}
              <kbd className="px-1 py-0.5 rounded bg-subtle border border-line text-[10px] font-mono">
                F10
              </kbd>{" "}
              Payment
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={handleClear}
              disabled={Loading}
              title="Clear form"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 border-danger/30 bg-white text-danger text-sm font-bold hover:bg-dangerSoft disabled:opacity-50 transition-colors active:scale-[.98]"
            >
              <FiXCircle size={18} />
              Clear
            </button>
            <button
              type="button"
              onClick={(e) => onSubmit(e, 2)}
              disabled={Loading}
              title="Ctrl+Shift+Enter"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border-2 border-main bg-white text-main text-sm font-bold hover:bg-main/5 disabled:opacity-50 transition-colors"
            >
              <FiPrinter size={18} />
              Save &amp; print
            </button>
            <button
              type="button"
              onClick={(e) => onSubmit(e, 1)}
              disabled={Loading}
              title="Ctrl+Enter · Enter Enter"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-main text-white text-sm font-bold hover:bg-mainHover shadow-md disabled:opacity-50 transition-colors active:scale-[.98]"
            >
              <FiSend size={18} />
              Save invoice
            </button>
          </div>
        </div>
        {Loading && (
          <div className="mt-2">
            <FetchingLoading />
          </div>
        )}
      </footer>

      {thermalReceiptData && (
        <div
          aria-hidden
          className="fixed pointer-events-none opacity-0"
          style={{ left: -10000, top: 0 }}
        >
          <ThermalCustomerInvoice
            ref={thermalPrintRef}
            data={{ ...thermalReceiptData, printMode: thermalPrintMode }}
          />
        </div>
      )}
    </div>
  );
};

export default CreateInvoice;
